// InvestmentGrid.jsx
import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    Chip,
    Grid,
    IconButton,
    Popover,
    Skeleton,
    Typography,
} from "@mui/material";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import Heading from "../heading";

const TEAL = "#24CAC1";
const TEAL_DARK = "#0d8196";

const CategoryCardsSkeleton = () => {
    return (
        <Grid container spacing={3}>
            {Array.from({ length: 6 }).map((_, index) => (
                <Grid item xs={12} md={6} key={index}>
                    <Box
                        sx={{
                            borderRadius: 5,
                            bgcolor: "#f5f5f5",
                            px: 3,
                            py: 2.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        {/* Left: icon + title */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Skeleton variant="circular" width={40} height={40} />
                            <Skeleton variant="text" width={140} height={24} />
                        </Box>

                        {/* Right: round arrow badge */}
                        <Skeleton variant="circular" width={32} height={32} />
                    </Box>
                </Grid>
            ))}
        </Grid>
    );
};

export default function InvestmentGrid({ loader, selected, setSelected, data, value, onChange }) {


    const handleSelect = (k) => {
        setSelected(k);
        onChange?.(k);
    };

    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    // ⏱ auto-close after 2 seconds
    useEffect(() => {
        if (!anchorEl) return;

        const timer = setTimeout(() => {
            setAnchorEl(null);
        }, 700);

        return () => clearTimeout(timer); // cleanup if user closes earlier / reopens
    }, [anchorEl]);

    return (
        <Box
            className="container"
            sx={{
                maxWidth: "1040px !important",
            }}
        >
            <Heading title="Investments" subtitle="Choose which suits you better" />

            <Grid container spacing={3}>
                {
                    loader ? <CategoryCardsSkeleton /> :
                        (data ?? [])
                            .filter((t) => t?.visibility === true) // keep only visible items
                            .map((item) => {
                                const isActive = selected === item.key;

                                const isCommingSoon = Number(item?.availabilityStatus) === 1;

                                return (
                                    <Grid item xs={12} sm={6} lg={6} key={item.key}>
                                        <Card
                                            role="button"
                                            aria-pressed={isActive}
                                            // onClick={() => handleSelect(item.key)}
                                            onClick={(e) => {
                                                // 👇 your condition: if isCommingSoon is false => show popover
                                                if (!isCommingSoon) {
                                                    setAnchorEl(e.currentTarget); // ✅ anchor = clicked card
                                                } else {
                                                    handleSelect(item.key);       // normal select
                                                }
                                            }}
                                            elevation={0}
                                            sx={{
                                                position: "relative",
                                                cursor: "pointer",
                                                minHeight: 120,
                                                p: { xs: 2, md: 2 },
                                                borderRadius: "22px",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 2.5,

                                                /* --- INACTIVE --- */
                                                ...(isActive
                                                    ? {}
                                                    : {
                                                        border: "1px solid #EBEBEB",
                                                        backgroundColor: "#EBEBEB",
                                                        // boxShadow: "inset 0 1px 0 rgba(255,255,255,.8)",
                                                    }),

                                                /* --- ACTIVE (gradient border + white fill) --- */
                                                ...(isActive && {
                                                    border: "2px solid transparent",
                                                    background: `
                          linear-gradient(#ffffff, #ffffff) padding-box,
                          linear-gradient(180deg, ${TEAL} 0%, ${TEAL_DARK} 100%) border-box
                        `,
                                                    boxShadow: "0 12px 28px rgba(0,0,0,.10)",
                                                }),

                                                transition:
                                                    "transform .18s ease, box-shadow .18s ease, border-color .18s ease, background .18s ease",
                                                "&:hover": {
                                                    transform: "translateY(-2px)",
                                                    // boxShadow: isActive
                                                    //     ? "0 14px 34px rgba(0,0,0,.12)"
                                                    //     : "0 8px 18px rgba(0,0,0,.06)",
                                                },

                                                /* soft top glow for ACTIVE */
                                                ...(isActive && {
                                                    "&::after": {
                                                        content: '""',
                                                        position: "absolute",
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        height: 10,
                                                        borderRadius: "18px 18px 0 0",
                                                        background:
                                                            "linear-gradient(180deg, rgba(36,202,193,.28) 0%, rgba(36,202,193,0) 100%)",
                                                        pointerEvents: "none",
                                                    },
                                                }),
                                            }}
                                        >
                                            {/* Icon box (fixed -> aligned rows) */}
                                            <Box sx={{ position: "relative", width: 88, height: 64, flex: "0 0 88px", ml: 0.5 }}>
                                                {item?.badge && isActive && (
                                                    <Chip
                                                        size="small"
                                                        label={item?.badge}
                                                        sx={{
                                                            position: "absolute",
                                                            top: -18,
                                                            left: 14,
                                                            height: 24,
                                                            fontSize: 12,
                                                            fontWeight: 900,
                                                            color: "#fff",
                                                            px: 1,
                                                            borderRadius: 1,
                                                            background: `linear-gradient(180deg, ${TEAL} 0%, ${TEAL_DARK} 100%)`,
                                                            boxShadow: "0 6px 18px rgba(36,202,193,.35)",
                                                        }}
                                                    />
                                                )}

                                                <Box
                                                    component="img"
                                                    src={`${process.env.IMAGE_BASE_URL}/${item?.image}`}
                                                    alt={item?.name}
                                                    sx={{
                                                        position: "absolute",
                                                        inset: 0,
                                                        objectFit: "contain",
                                                        height: "70px",
                                                        // filter: isActive ? "none" : "grayscale(100%)",
                                                        // opacity: isActive ? 1 : 0.7,
                                                        opacity: 1,
                                                    }}
                                                />
                                            </Box>

                                            {/* Coming Soon Overlay */}
                                            {/* {!isCommingSoon && (
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        inset: 0,
                                                        zIndex: 2,
                                                        backgroundColor: "rgba(0,0,0,0.55)",
                                                        display: "flex",
                                                        alignItems: "start",
                                                        justifyContent: "center",
                                                        borderRadius: "22px",
                                                        // paddingLeft: "20px", 
                                                    }}
                                                >
                                                    <Box
                                                        component="img"
                                                        // src="/img/header/comingsoon.svg"
                                                        src={`${process.env.IMAGE_BASE_URL}/${item?.disableImage}`}
                                                        alt="Coming Soon"
                                                        sx={{
                                                            width: "50%",
                                                            maxHeight: '100%',
                                                            // marginRight: '20%'
                                                            // maxWidth: 220,

                                                        }}
                                                    />
                                                </Box>
                                            )} */}


                                            {/* Title */}
                                            <Typography
                                                sx={{
                                                    fontWeight: 600,
                                                    letterSpacing: 0.6,
                                                    fontSize: { xs: 18, md: 22 },
                                                    textTransform: "uppercase",
                                                    flex: 1,

                                                    // gradient text when active, normal color otherwise
                                                    ...(isActive
                                                        ? {
                                                            backgroundImage: "linear-gradient(180deg, #7fd0cb 0%, #0f7f8a 100%)",
                                                            backgroundClip: "text",
                                                            WebkitBackgroundClip: "text",
                                                            color: "primary gradient",
                                                            WebkitTextFillColor: "transparent",
                                                            display: "inline-block", // ensure background-clip works consistently
                                                        }
                                                        : {
                                                            color: "black", // normal text color
                                                            transition: "all 0.3s ease",
                                                            "&:hover": {
                                                                background: "linear-gradient(180deg, #7fd0cb 0%, #0f7f8a 100%)",
                                                                WebkitBackgroundClip: "text",
                                                                WebkitTextFillColor: "transparent",
                                                            },
                                                        }),
                                                }}
                                            >
                                                {item?.name}
                                            </Typography>


                                            {/* Right chevron */}
                                            <IconButton
                                                size="small"
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: "50%",
                                                    ml: "auto",
                                                    border: isActive ? "none" : "1px solid #e6eaee",
                                                    color: "#fff",
                                                    background: isActive
                                                        ? `linear-gradient(180deg, #7fd0cb 0%, #0f7f8a 100%)`
                                                        : "linear-gradient(to bottom, #7fd0cb 0%, #0f7f8a 100%)",
                                                    // boxShadow: isActive ? "0 6px 16px rgba(36,202,193,.35)" : "none",
                                                }}
                                            >
                                                <ArrowDownwardRoundedIcon />
                                            </IconButton>
                                        </Card>
                                    </Grid>
                                );
                            })
                }
                {/* 🔻 Single Popover, outside the map */}
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                    transformOrigin={{ vertical: "top", horizontal: "center" }}
                    PaperProps={{
                        sx: { px: 1.5, py: 1, borderRadius: 2 },
                        elevation: 3,
                    }}
                >
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                        Coming soon
                    </Typography>
                </Popover>
            </Grid>
        </Box>
    );
}
